import * as React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, ClickAwayListener, IconButton, Paper, Popper, Stack, Typography } from '@mui/material';
import { Quality } from '../../commands/practitioner/enums/CallQuality';
import { summarizePreflight } from './NetworkQuality';
import { colorFor } from '../../components/UI/NetworkQualityIndicator/style';
import { networkReportPopoverStyles, statusDotStyle } from './NetworkReportPopover.style';
import { useTranslation } from 'react-i18next';

interface NetworkReportPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  quality: Quality;
  report: any;
  onClose: () => void;
}

const qualityLabelByState: Record<Quality, string> = {
  [Quality.Idle]: 'internet-test-paper-idle',
  [Quality.Testing]: 'internet-test-paper-testing',
  [Quality.Excellent]: 'internet-test-paper-excellent',
  [Quality.Good]: 'internet-test-paper-good',
  [Quality.Fair]: 'internet-test-paper-fair',
  [Quality.Poor]: 'internet-test-paper-poor',
  [Quality.Error]: 'internet-test-paper-error',
};

const impactTextByState: Record<Quality, string> = {
  [Quality.Idle]: 'internet-test-paper-description-idle',
  [Quality.Testing]: 'internet-test-paper-description-testing',
  [Quality.Excellent]: 'internet-test-paper-description-excellent',
  [Quality.Good]: 'internet-test-paper-description-good',
  [Quality.Fair]: 'internet-test-paper-description-fair',
  [Quality.Poor]: 'internet-test-paper-description-poor',
  [Quality.Error]: 'internet-test-paper-description-error',
};

const tipsByState: Partial<Record<Quality, string[]>> = {
  [Quality.Fair]: ['internet-quality-tip1', 'internet-quality-tip2', 'internet-quality-tip3'],
  [Quality.Poor]: ['internet-quality-tip1', 'internet-quality-tip2', 'internet-quality-tip3'],
  [Quality.Error]: ['internet-quality-error'],
};

const fallbackTextByKey: Partial<Record<string, string>> = {
  'internet-test-paper-title': 'Connection',
  'internet-test-paper-idle': 'Idle',
  'internet-test-paper-testing': 'Testing',
  'internet-test-paper-excellent': 'Excellent',
  'internet-test-paper-good': 'Good',
  'internet-test-paper-fair': 'Fair',
  'internet-test-paper-poor': 'Poor',
  'internet-test-paper-error': 'Error',
  'internet-test-paper-description-idle': 'Run the test to check whether your connection is ready for a call.',
  'internet-test-paper-description-testing': 'We are measuring your connection quality now.',
  'internet-test-paper-description-excellent': 'Your connection looks excellent for video and audio.',
  'internet-test-paper-description-good': 'Your connection looks good for video and audio.',
  'internet-test-paper-description-fair': 'Your connection may cause occasional quality issues.',
  'internet-test-paper-description-poor': 'Your connection is likely to cause interruptions.',
  'internet-test-paper-description-error': 'We could not complete the connection test.',
  'quick-fixes': 'Quick fixes',
  'internet-quality-tip1': 'Move closer to your Wi-Fi router or use a wired connection.',
  'internet-quality-tip2': 'Pause downloads, streaming, or other high-bandwidth apps.',
  'internet-quality-tip3': 'Ask others on the network to reduce heavy internet use.',
  'internet-quality-error': 'Retry the test and check whether your connection or firewall blocks the test.',
  'technical-report': 'Technical report',
};

const showMsCompact = (value: number | null) => (value == null ? '--' : `${value}ms`);
const showPct = (value: number | null) => (value == null ? '--' : `${value}%`);
const showMbps = (value: number | null) => (value == null ? '--' : `${(value / 1000).toFixed(1)} Mbps`);

export default function NetworkReportPopover(props: NetworkReportPopoverProps) {
  const { open, anchorEl, quality, report, onClose } = props;
  const { t, i18n } = useTranslation();
  const isOpen = open && !!anchorEl;
  if (!report || !isOpen) return null;

  const translate = (key: string) => {
    const fallback = fallbackTextByKey[key];
    return fallback && !i18n.exists(key) ? fallback : t(key);
  };

  const summary = summarizePreflight(report);
  const tips = tipsByState[quality] ?? [];
  const qualityLabel = qualityLabelByState[quality];
  const statusColor = colorFor(quality);

  return (
    <Popper
      open={isOpen}
      anchorEl={anchorEl}
      placement="bottom-end"
      disablePortal={false}
      modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
      sx={networkReportPopoverStyles.popper}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper elevation={6} sx={networkReportPopoverStyles.paper}>
          <Box sx={networkReportPopoverStyles.container}>
            <Box sx={networkReportPopoverStyles.scrollArea}>
              <Stack sx={networkReportPopoverStyles.contentStack}>
                <Stack sx={networkReportPopoverStyles.headerRow}>
                  <Stack sx={networkReportPopoverStyles.headerLeft}>
                    <Box sx={statusDotStyle(statusColor)} />
                    <Typography variant="subtitle2" sx={networkReportPopoverStyles.connectionTitle}>
                      {`${translate('internet-test-paper-title')} ${translate(qualityLabel)}`}
                    </Typography>
                  </Stack>
                  <IconButton
                    size="small"
                    aria-label="Close"
                    onClick={onClose}
                    sx={networkReportPopoverStyles.closeButton}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>

                <Typography variant="body2" sx={networkReportPopoverStyles.impactText}>
                  {translate(impactTextByState[quality])}
                </Typography>

                {tips.length > 0 ? (
                  <Box>
                    <Typography variant="subtitle2" sx={networkReportPopoverStyles.quickFixTitle}>
                      {translate('quick-fixes')}
                    </Typography>
                    <Box component="ul" sx={networkReportPopoverStyles.quickFixList}>
                      {tips.map(tip => (
                        <Box key={tip} component="li" sx={networkReportPopoverStyles.quickFixItem}>
                          <Typography variant="caption" sx={networkReportPopoverStyles.quickFixText}>
                            {translate(tip)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}
              </Stack>
            </Box>

            <Box sx={networkReportPopoverStyles.technicalInfoFooter}>
              <Typography variant="caption" sx={networkReportPopoverStyles.technicalInfoLabel}>
                {translate('technical-report')}
              </Typography>
              <Typography variant="caption" sx={networkReportPopoverStyles.technicalInfoText}>
                Latency: {showMsCompact(summary.latency.avg)} | jitter: {showMsCompact(summary.jitter.avg)} | loss:{' '}
                {showPct(summary.loss.avgPct)} | bitrate: {showMbps(summary.bitrate.avgKbps)}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
}
